import {BLOG_PAGE_SIZE} from 'src/configs/constants'
import {Post} from 'src/types/__sanitySchema__'
import {TPost} from 'src/types/blog'
import {getImageBuilderFor} from 'src/utils/sanityImageBuilder'

import sanityClient from '../lib/sanityClient'

/**
 * Uses to make requests for the blog data in the getStatic/Server props functions
 *
 * @class BlogModel
 */
class BlogModel {
	/**
	 * Get all posts
	 * @returns {Promise<TPost[]>}
	 * @memberof BlogModel
	 */
	getAll = async (): Promise<TPost[]> => {
		const posts = await sanityClient.fetch<Post[]>('*[_type == "post"] | order(_createdAt desc)')
		return posts.map((post) => this._transformPost(post))
	}

	/**
	 * Get paginated posts
	 * @returns {Promise<{posts: TPost[], count: number}>}
	 */
	get = async ({page = 1, pageSize = BLOG_PAGE_SIZE}): Promise<{posts: TPost[]; count: number}> => {
		const skip = (page - 1) * pageSize

		const {count, posts} = await sanityClient.fetch<{posts: Post[]; count: number}>(
			`{
				"posts": *[_type == "post"] | order(_createdAt desc) [${skip}...${skip + pageSize}],
				"count": count(*[_type == "post"])
			}`,
		)

		return {count, posts: posts.map((post) => this._transformPost(post))}
	}

	/**
	 * Function to get one post by slug
	 * @returns - post data if found
	 * @memberof BlogModel
	 */
	getBySlug = async (slug: string): Promise<TPost | null> => {
		const query = `*[_type == "post" && slug.current == "${slug}"]`
		const [post] = await sanityClient.fetch<Post[]>(query)

		return !!post ? this._transformPost(post) : null
	}

	/**
	 * Transform main image field in the post to have URL string
	 * @memberof BlogModel
	 */
	private _transformPost = (post: Post): TPost => {
		const width = 628
		const height = 411
		const alt = post.mainImage?.alt || post.title || ''
		const imageUrl =
			(post.mainImage && getImageBuilderFor(post.mainImage))?.width(width).height(height).url() ||
			null

		return {...post, mainImage: {url: imageUrl, alt, width, height}}
	}
}

export default new BlogModel()
